import { AccountData, GroupChat, Event, TicketV2 } from "@markit/common.types";
import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";
import {
  AsyncLoadingState,
  createAsyncErrorState,
  createAsyncLoadedState,
  createAsyncLoadingState,
  createAsyncNotLoadedState,
} from "../../utils/asyncLoadingState";
import {
  getEventData,
  getGroupData,
  getTicketData,
  getUserData,
} from "../../utils/FirebaseUtils";
import { AppState } from "../store";

/**
 * Stores all the loaded users/events/groups/etc
 */
export interface DataState {
  groupChats: { [groupChatId: string]: AsyncLoadingState<GroupChat> };
  users: { [userId: string]: AsyncLoadingState<AccountData> };
  events: { [eventId: string]: AsyncLoadingState<Event> };
  tickets: { [ticketId: string]: AsyncLoadingState<TicketV2> };
  userByUsername: { [username: string]: AsyncLoadingState<string> }; // Maps username to userId
}

export const initialDataState: DataState = {
  groupChats: {},
  users: {},
  events: {},
  tickets: {},
  userByUsername: {},
};

// How long to wait for a Firestore getDoc before retrying.
// On iOS, the WebSocket handshake can take 8–10s on a cold start. If the
// first attempt times out, the connection is usually established by the time
// the retry fires, so the retry succeeds immediately.
const FIRESTORE_TIMEOUT_MS = 8000;

function createActionsAndReducerForData<
  T extends keyof DataState,
  J extends DataState[T][string] extends AsyncLoadingState<infer X> ? X : never,
>(key: T, queryFn: (id: string) => Promise<J | undefined>) {
  const loadFn = createAsyncThunk(
    `data/${key}`,
    async (id: string, { rejectWithValue }) => {
      if (id === "") {
        return rejectWithValue("cannot load id of empty string");
      }

      const fetchWithTimeout = () =>
        Promise.race([
          queryFn(id),
          new Promise<never>((_resolve, reject) =>
            setTimeout(
              () => reject(new Error("firestore_timeout")),
              FIRESTORE_TIMEOUT_MS
            )
          ),
        ]);

      try {
        const response = await fetchWithTimeout();
        if (response) {
          // console.log(`loaded ${key} ${id}`, response);
          return response;
        }
        return rejectWithValue(response);
      } catch (firstError) {
        // On a timeout, retry once — by now the WebSocket is usually established
        if (
          firstError instanceof Error &&
          firstError.message === "firestore_timeout"
        ) {
          try {
            const response = await fetchWithTimeout();
            if (response) return response;
            return rejectWithValue(response);
          } catch (retryError) {
            return rejectWithValue(retryError);
          }
        }
        return rejectWithValue(firstError);
      }
    }
  );

  const buildReducer = (builder: ActionReducerMapBuilder<DataState>) => {
    builder.addCase(loadFn.pending, (state, action) => {
      const objectId = action.meta.arg;
      return {
        ...state,
        [key]: {
          ...state[key],
          [objectId]: createAsyncLoadingState(),
        },
      };
    });
    builder.addCase(loadFn.fulfilled, (state, action) => {
      const objectId = action.meta.arg;
      const object = action.payload;
      if (!objectId) {
        return state;
      }
      return {
        ...state,
        [key]: {
          ...state[key],
          [objectId]: createAsyncLoadedState(object),
        },
      };
    });
    builder.addCase(loadFn.rejected, (state, action) => {
      const objectId = action.meta.arg;
      return {
        ...state,
        [key]: {
          ...state[key],
          [objectId]: createAsyncErrorState(),
        },
      };
    });
  };

  return { loadFn, buildReducer };
}

function createActionsAndReducerForEventData<
  T extends keyof DataState,
  J extends DataState[T][string] extends AsyncLoadingState<infer X> ? X : never,
>(
  key: T,
  queryFn: (eventItem: {
    eventId: string;
    itemId: string;
  }) => Promise<J | undefined>
) {
  const loadFn = createAsyncThunk(
    `data/${key}`,
    async (
      { eventId, itemId }: { eventId: string; itemId: string },
      { rejectWithValue }
    ) => {
      if (eventId === "" || itemId === "") {
        return rejectWithValue("cannot load id of empty string");
      }
      try {
        const response = await queryFn({ eventId, itemId });
        if (response) {
          console.log(`loaded ${key} ${itemId}`, response);
          return response;
        }
        return rejectWithValue(response);
      } catch (error) {
        return rejectWithValue(error);
      }
    }
  );

  const buildReducer = (builder: ActionReducerMapBuilder<DataState>) => {
    builder.addCase(loadFn.pending, (state, action) => {
      const objectId = action.meta.arg;
      return {
        ...state,
        [key]: {
          ...state[key],
          [objectId.itemId]: createAsyncLoadingState(),
        },
      };
    });
    builder.addCase(loadFn.fulfilled, (state, action) => {
      const objectId = action.meta.arg;
      const object = action.payload;
      if (!objectId) {
        return state;
      }
      return {
        ...state,
        [key]: {
          ...state[key],
          [objectId.itemId]: createAsyncLoadedState(object),
        },
      };
    });
    builder.addCase(loadFn.rejected, (state, action) => {
      const objectId = action.meta.arg;
      return {
        ...state,
        [key]: {
          ...state[key],
          [objectId.itemId]: createAsyncErrorState(),
        },
      };
    });
  };

  return { loadFn, buildReducer };
}

const gatherTicketData = async (ticket: {
  eventId: string;
  itemId: string;
}) => {
  const ticketData = await getTicketData(ticket.eventId, ticket.itemId);
  if (ticketData) {
    return ticketData;
  }
  return undefined;
};

export const { loadFn: loadGroup, buildReducer: buildGroupChatReducer } =
  createActionsAndReducerForData("groupChats", getGroupData);
export const { loadFn: loadUser, buildReducer: buildUserReducer } =
  createActionsAndReducerForData("users", getUserData);
export const { loadFn: loadEvent, buildReducer: buildEventReducer } =
  createActionsAndReducerForData("events", getEventData);
export const { loadFn: loadTicket, buildReducer: buildTicketReducer } =
  createActionsAndReducerForEventData("tickets", gatherTicketData);

export const DataLoaders = {
  user: loadUser,
  group: loadGroup,
  event: loadEvent,
  ticket: loadTicket,
};

// Create a new action to set loading state for username lookup
export const setUsernameLoadingState = createAction<string>(
  "data/setUsernameLoadingState"
);

// Create a new action to set error state for username lookup
export const setUsernameErrorState = createAction<string>(
  "data/setUsernameErrorState"
);

// Create a new action to clear username state (used when user is successfully loaded)
export const clearUsernameState = createAction<string>(
  "data/clearUsernameState"
);

// Create a builder function for username state management
export const buildUsernameReducer = (
  builder: ActionReducerMapBuilder<DataState>
) => {
  // Add reducer for the username loading state
  builder.addCase(setUsernameLoadingState, (state, action) => {
    const username = action.payload;
    return {
      ...state,
      userByUsername: {
        ...state.userByUsername,
        [username]: createAsyncLoadingState(),
      },
    };
  });

  // Add reducer for the username error state
  builder.addCase(setUsernameErrorState, (state, action) => {
    const username = action.payload;
    return {
      ...state,
      userByUsername: {
        ...state.userByUsername,
        [username]: createAsyncErrorState(),
      },
    };
  });

  // Add reducer to clear username state
  builder.addCase(clearUsernameState, (state, action) => {
    const username = action.payload;
    const newUserByUsername = { ...state.userByUsername };
    delete newUserByUsername[username];

    return {
      ...state,
      userByUsername: newUserByUsername,
    };
  });
};

export const dataSlice = createSlice({
  name: "data",
  initialState: initialDataState,
  reducers: {},
  extraReducers: (builder) => {
    buildGroupChatReducer(builder);
    buildUserReducer(builder);
    buildEventReducer(builder);
    buildTicketReducer(builder);
    buildUsernameReducer(builder);
  },
});

export const dataReducer = dataSlice.reducer;

// Selectors
export const getLoadedGroupChat = (state: AppState, groupChatId: string) =>
  state.data.groupChats[groupChatId] ?? createAsyncNotLoadedState();
export const getLoadedUser = (state: AppState, userId: string) =>
  state.data.users[userId] ?? createAsyncNotLoadedState();

// Updated to check the username loading state first
export const getLoadedUserByUsername = (
  state: AppState,
  username: string
): AsyncLoadingState<AccountData> => {
  // First check if we're loading or have an error for this username
  const usernameState = state.data.userByUsername[username];
  if (usernameState) {
    if (usernameState.isLoading) {
      return createAsyncLoadingState<AccountData>();
    } else if (usernameState.isError) {
      return createAsyncErrorState<AccountData>();
    }
  }

  // Then look for a user with this username
  const foundUser = Object.values(state.data.users).find(
    (user) => user.data?.username === username
  );

  if (foundUser) {
    return foundUser;
  }

  // If we don't have a username state and didn't find a user,
  // return loading state since we're likely still loading
  if (!usernameState && username) {
    return createAsyncLoadingState<AccountData>();
  }

  // Only return error state if we explicitly set an error
  return createAsyncErrorState<AccountData>();
};

export const getLoadedEvent = (state: AppState, eventId: string) =>
  state.data.events[eventId] ?? createAsyncNotLoadedState();
export const getLoadedUsers = (state: AppState, userIds: string[]) =>
  userIds.map((userId) => getLoadedUser(state, userId));
export const getAllLoadedEvents = (state: AppState) =>
  Object.values(state.data.events)
    .map((val) => val.data)
    .filter((event) => event != null) as Event[];
export const getLoadedTicket = (state: AppState, ticketId: string) =>
  state.data.tickets[ticketId] ?? createAsyncNotLoadedState();

export const getDataState = (state: AppState) => state;
